import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import * as XLSX from 'xlsx';
import { LocationItem, LocationService } from '../core/services/location.service';
import {
  MealSummaryReportRow,
  MealSummaryReportService,
} from '../core/services/meal-summary-report.service';
import { getProgramId } from '../shared/utils/storage-utils';
import {
  buildReportDownloadSuffix,
  formatReportDateDisplay,
  formatReportDatePickerLabel,
  openNativeDatePicker,
  resolveReportDateRange,
  toReportDateInputValue,
} from '../shared/utils/report-date-range';

@Component({
  selector: 'meal-summary-report-component',
  standalone: true,
  templateUrl: './meal-summary-report.component.html',
  styleUrls: ['./meal-summary-report.component.css'],
  imports: [CommonModule, FormsModule, RouterModule],
})
export class MealSummaryReportComponent implements OnInit {
  isInitialLoading = true;
  isRefreshing = false;
  errorMessage: string | null = null;

  centers: LocationItem[] = [];
  filterCenterName = '';
  /** Same as Activity Report: toolbar uses custom date range only. */
  private readonly timeframe = 'custom' as const;

  customFromDate = '';
  customToDate = '';

  rows: MealSummaryReportRow[] = [];

  constructor(
    private readonly router: Router,
    private readonly locationService: LocationService,
    private readonly reportService: MealSummaryReportService,
  ) {}

  ngOnInit(): void {
    const token = sessionStorage.getItem('token');
    if (!token) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    const today = toReportDateInputValue(new Date());
    this.customFromDate = today;
    this.customToDate = today;

    this.locationService.getActiveLocations().subscribe({
      next: (response) => {
        this.centers = response.locations ?? [];
        this.loadReport();
      },
      error: () => {
        this.centers = [];
        this.loadReport();
      },
    });
  }

  goBack(): void {
    void this.router.navigate(['/manage-services/senior-center/reports']);
  }

  onFiltersChanged(): void {
    this.loadReport({ silent: true });
  }

  formatDate(value: string | null | undefined): string {
    return formatReportDateDisplay(value);
  }

  openDatePicker(event: Event): void {
    openNativeDatePicker(event);
  }

  formatDatePickerLabel(value: string | null | undefined): string {
    return formatReportDatePickerLabel(value);
  }

  onDownload(): void {
    if (!this.rows.length) {
      return;
    }

    const sheetRows = this.rows.map((row) => ({
      'User Name': row.userName,
      Meal: row.meal,
      Type: row.type,
      Drink: row.drink,
      Center: row.center,
      Date: this.formatDate(row.mealDate),
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Meal Summary Report');
    XLSX.writeFile(workbook, `meal-summary-report-${this.downloadSuffix()}.xlsx`);
  }

  trackByRow(index: number, row: MealSummaryReportRow): string {
    return `${row.userName}|${row.meal}|${row.center}|${row.mealDate ?? ''}|${index}`;
  }

  private downloadSuffix(): string {
    return buildReportDownloadSuffix({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
  }

  private loadReport(options: { silent?: boolean } = {}): void {
    const range = resolveReportDateRange({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
    if (!range) {
      this.errorMessage = 'Select a valid date range.';
      this.isInitialLoading = false;
      return;
    }

    if (!options.silent) {
      this.isInitialLoading = true;
    } else {
      this.isRefreshing = true;
    }
    this.errorMessage = null;

    this.reportService
      .getReport({
        fromDate: range.fromDate,
        toDate: range.toDate,
        center: this.filterCenterName || null,
        programId: getProgramId() ?? 1000,
      })
      .subscribe({
        next: (response) => {
          this.rows = response.rows ?? [];
          this.isInitialLoading = false;
          this.isRefreshing = false;
        },
        error: (err) => {
          this.rows = [];
          this.isInitialLoading = false;
          this.isRefreshing = false;
          this.errorMessage =
            err?.error?.message ||
            err?.error?.Message ||
            'Unable to load meal summary report. Please try again.';
        },
      });
  }
}
